// User Management - Search, view, ban, and manage user roles
import React, { useState, useEffect } from 'react';
import { db, FUNCTIONS_URL } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    updateDoc,
    getDoc,
    setDoc,
    orderBy,
    limit
} from 'firebase/firestore';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Avatar,
    Chip,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    LinearProgress,
    IconButton,
    InputAdornment,
    Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import BlockIcon from '@mui/icons-material/Block';
import VerifiedIcon from '@mui/icons-material/Verified';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HistoryIcon from '@mui/icons-material/History';
import WarningIcon from '@mui/icons-material/Warning';
import StarIcon from '@mui/icons-material/Star';

const UserManagement = () => {
    const { getIdToken, isAdmin } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchType, setSearchType] = useState('email');
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userHistory, setUserHistory] = useState([]);
    const [roleDialog, setRoleDialog] = useState({ open: false, user: null });
    const [selectedRole, setSelectedRole] = useState('none');
    const [badgeDialog, setBadgeDialog] = useState({ open: false, user: null });
    const [selectedBadgeRole, setSelectedBadgeRole] = useState('doctor');
    const [subscriptionDialog, setSubscriptionDialog] = useState({ open: false, user: null });
    const [selectedPlan, setSelectedPlan] = useState('premium');
    const [alert, setAlert] = useState(null);

    const mapUsers = (docs) => docs.map((d) => ({ ...d.data(), id: d.id }));

    const loadRecentUsers = async () => {
        setLoading(true);
        try {
            const snapshot = await getDocs(query(collection(db, 'users'), limit(25)));
            setUsers(mapUsers(snapshot.docs));
        } catch (error) {
            console.error('Load users error:', error);
            setAlert({ severity: 'error', message: 'Failed to load users: ' + error.message });
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const snapshot = await getDocs(query(collection(db, 'users'), limit(25)));
                if (!cancelled) setUsers(snapshot.docs.map((d) => ({ ...d.data(), id: d.id })));
            } catch (error) {
                console.error('Load users error:', error);
                if (!cancelled) {
                    setAlert({ severity: 'error', message: 'Failed to load users: ' + error.message });
                    setUsers([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    const handleSearch = async () => {
        if (!searchQuery.trim() && searchType !== 'badge') {
            setHasSearched(false);
            loadRecentUsers();
            return;
        }
        setLoading(true);
        setHasSearched(true);
        setUsers([]);

        try {
            let q;
            if (searchType === 'email') {
                q = query(collection(db, 'users'), where('email', '==', searchQuery.trim()));
            } else if (searchType === 'username') {
                const term = searchQuery.trim();
                const [byDisplay, byUsername] = await Promise.all([
                    getDocs(query(collection(db, 'users'), where('displayName', '==', term))),
                    getDocs(query(collection(db, 'users'), where('username', '==', term))).catch(() => ({ docs: [] })),
                ]);
                const merged = new Map();
                [...byDisplay.docs, ...byUsername.docs].forEach((d) => merged.set(d.id, { ...d.data(), id: d.id }));
                setUsers([...merged.values()]);
                setLoading(false);
                return;
            } else if (searchType === 'uid') {
                const userDoc = await getDoc(doc(db, 'users', searchQuery.trim()));
                setUsers(userDoc.exists() ? [{ ...userDoc.data(), id: userDoc.id }] : []);
                setLoading(false);
                return;
            } else if (searchType === 'badge') {
                q = query(collection(db, 'users'), where('hasExpertBadge', '==', true));
            }

            if (q) {
                const snapshot = await getDocs(q);
                setUsers(mapUsers(snapshot.docs));
            }
        } catch (error) {
            console.error('Search error:', error);
            setAlert({ severity: 'error', message: 'Search failed: ' + error.message });
        } finally {
            setLoading(false);
        }
    };

    const loadUserHistory = async (user) => {
        setSelectedUser(user);
        setUserHistory([]);

        try {
            // Load reports against this user
            const reportsQuery = query(
                collection(db, 'moderation_reports'),
                where('reportedUserId', '==', user.id),
                orderBy('createdAt', 'desc'),
                limit(20)
            );
            const reportsSnapshot = await getDocs(reportsQuery);
            const reports = reportsSnapshot.docs.map(doc => ({
                type: 'report',
                id: doc.id,
                ...doc.data()
            }));

            // Load recent messages (limited)
            // This would require collectionGroup query for messages

            setUserHistory(reports);
        } catch (error) {
            console.error('Error loading history:', error);
        }
    };

    const callSetAdminClaims = async (targetUserId, role) => {
        const token = await getIdToken();
        const response = await fetch(`${FUNCTIONS_URL}/setAdminClaims`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ targetUserId, role })
        });
        if (!response.ok) {
            throw new Error(await response.text());
        }
    };

    const handleSetRole = async () => {
        if (!roleDialog.user) return;

        try {
            await callSetAdminClaims(roleDialog.user.id, selectedRole);
            setAlert({ severity: 'success', message: `Role updated to ${selectedRole}` });
            setRoleDialog({ open: false, user: null });
            handleSearch();
        } catch (error) {
            setAlert({ severity: 'error', message: 'Failed to set role: ' + error.message });
        }
    };

    const handleSetExpertBadge = async (grant) => {
        if (!badgeDialog.user) return;

        try {
            const currentRole = badgeDialog.user.role;
            if (grant) {
                await callSetAdminClaims(badgeDialog.user.id, selectedBadgeRole);
            } else if (['doctor', 'midwife', 'nurse'].includes(currentRole)) {
                await callSetAdminClaims(badgeDialog.user.id, 'none');
            } else {
                await updateDoc(doc(db, 'users', badgeDialog.user.id), {
                    hasExpertBadge: false,
                    expertBadge: null,
                    expertBadgeType: null,
                    badgeUpdatedAt: new Date(),
                });
            }

            setAlert({
                severity: 'success',
                message: grant
                    ? `Expert badge granted (${selectedBadgeRole})`
                    : 'Badge removed',
            });
            setBadgeDialog({ open: false, user: null });
            handleSearch();
        } catch (error) {
            setAlert({ severity: 'error', message: 'Failed to update badge: ' + error.message });
        }
    };

    const handleGrantSubscription = async (plan) => {
        if (!subscriptionDialog.user) return;
        try {
            const expiry = new Date();
            expiry.setFullYear(expiry.getFullYear() + 1);
            const planId = plan === 'family' ? 'family_yearly' : plan === 'pro' ? 'pro_yearly' : 'premium_yearly';
            const userRef = doc(db, 'users', subscriptionDialog.user.id);
            await updateDoc(userRef, {
                subscriptionStatus: plan,
                subscriptionPlatform: 'admin_grant',
                subscriptionStartDate: new Date(),
                subscriptionExpiryDate: expiry,
                updatedAt: new Date(),
            });
            await setDoc(doc(db, 'users', subscriptionDialog.user.id, 'subscription', 'current'), {
                tier: plan,
                is_active: true,
                subscription_id: `admin_grant_${subscriptionDialog.user.id}`,
                customer_id: subscriptionDialog.user.id,
                expiry_date: expiry.getTime(),
                plan_id: planId,
                source: 'admin_grant',
                updated_at: new Date(),
            }, { merge: true });
            setAlert({ severity: 'success', message: `${plan} plan granted (1 year)` });
            setSubscriptionDialog({ open: false, user: null });
            handleSearch();
        } catch (error) {
            setAlert({ severity: 'error', message: 'Failed to grant subscription: ' + error.message });
        }
    };

    const handleRevokeSubscription = async (user) => {
        try {
            await updateDoc(doc(db, 'users', user.id), {
                subscriptionStatus: 'free',
                subscriptionPlatform: null,
                subscriptionExpiryDate: null,
                updatedAt: new Date(),
            });
            await setDoc(doc(db, 'users', user.id, 'subscription', 'current'), {
                tier: 'free',
                is_active: false,
                plan_id: null,
                source: 'admin_revoke',
                updated_at: new Date(),
            }, { merge: true });
            setAlert({ severity: 'success', message: 'Subscription revoked' });
            handleSearch();
        } catch (error) {
            setAlert({ severity: 'error', message: 'Failed to revoke subscription: ' + error.message });
        }
    };

    const handleBanUser = async (user) => {
        try {
            const token = await getIdToken();
            await fetch(`${FUNCTIONS_URL}/setAdminClaims`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    targetUserId: user.id,
                    role: 'banned'
                })
            });

            // Also update Firestore
            await updateDoc(doc(db, 'users', user.id), {
                banned: true,
                bannedAt: new Date()
            });

            setAlert({ severity: 'success', message: 'User banned successfully' });
            handleSearch();
        } catch (error) {
            setAlert({ severity: 'error', message: 'Failed to ban user: ' + error.message });
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Unknown';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString();
    };

    return (
        <Box>
            {/* Header */}
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                User Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Search users, manage roles, and review activity
            </Typography>

            {/* Alert */}
            {alert && (
                <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            {/* Search */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Search by</InputLabel>
                            <Select
                                value={searchType}
                                label="Search by"
                                onChange={(e) => setSearchType(e.target.value)}
                            >
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="username">Username</MenuItem>
                                <MenuItem value="uid">User ID</MenuItem>
                                <MenuItem value="badge">Has Expert Badge</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleSearch}>
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Button variant="contained" onClick={handleSearch} disabled={loading}>
                            Search
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Results */}
            {!loading && users.length === 0 && (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 8 }}>
                        <PersonIcon sx={{ fontSize: 48, mb: 1, opacity: 0.35 }} />
                        <Typography variant="h6" gutterBottom>
                            {hasSearched ? 'No users found' : 'No users yet'}
                        </Typography>
                        <Typography color="text.secondary">
                            {hasSearched
                                ? 'Try a different username, email, or user ID.'
                                : 'Enter a username, email, or user ID to search, or wait for users to sign up.'}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {users.length > 0 && (
                <Card>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Joined</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar src={user.photoURL} sx={{ width: 32, height: 32 }}>
                                                {user.displayName?.[0] || user.email?.[0]}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {user.displayName || 'Anonymous'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {user.id.substring(0, 8)}...
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        {user.banned && <Chip size="small" label="Banned" color="error" />}
                                        {user.hasExpertBadge && (
                                            <Chip
                                                size="small"
                                                label="Expert"
                                                icon={<VerifiedIcon />}
                                                color="primary"
                                                sx={{ ml: 0.5 }}
                                            />
                                        )}
                                        {!user.banned && !user.hasExpertBadge && (
                                            <Chip size="small" label="Active" color="success" />
                                        )}
                                    </TableCell>
                                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={() => loadUserHistory(user)}
                                            title="View History"
                                        >
                                            <HistoryIcon />
                                        </IconButton>
                                        {isAdmin && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => { setRoleDialog({ open: true, user }); setSelectedRole('none'); }}
                                                    title="Set Role"
                                                    color="primary"
                                                >
                                                    <AdminPanelSettingsIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        const role = ['doctor', 'midwife', 'nurse'].includes(user.role)
                                                            ? user.role
                                                            : 'doctor';
                                                        setSelectedBadgeRole(role);
                                                        setBadgeDialog({ open: true, user });
                                                    }}
                                                    title="Expert Badge"
                                                    color="success"
                                                >
                                                    <VerifiedIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => { setSubscriptionDialog({ open: true, user }); setSelectedPlan('premium'); }}
                                                    title="Manage Subscription"
                                                    color="warning"
                                                >
                                                    <StarIcon />
                                                </IconButton>
                                                {!user.banned && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleBanUser(user)}
                                                        title="Ban User"
                                                        color="error"
                                                    >
                                                        <BlockIcon />
                                                    </IconButton>
                                                )}
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}

            {/* User History Dialog */}
            <Dialog
                open={!!selectedUser}
                onClose={() => setSelectedUser(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={selectedUser?.photoURL}>
                            {selectedUser?.displayName?.[0]}
                        </Avatar>
                        <Box>
                            <Typography variant="h6">{selectedUser?.displayName || 'Anonymous'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedUser?.email}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" gutterBottom>Reports Against User</Typography>
                    {userHistory.length === 0 ? (
                        <Typography color="text.secondary">No reports found</Typography>
                    ) : (
                        userHistory.map((item) => (
                            <Card key={item.id} variant="outlined" sx={{ mb: 1, p: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Chip size="small" label={item.reason} />
                                    <Typography variant="caption">{formatDate(item.createdAt)}</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mt: 1 }}>{item.note}</Typography>
                            </Card>
                        ))
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedUser(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Role Dialog */}
            <Dialog open={roleDialog.open} onClose={() => setRoleDialog({ open: false, user: null })}>
                <DialogTitle>Set User Role</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Admin / Moderator</strong> — grants dashboard login access.<br />
                        <strong>Doctor / Midwife / Nurse</strong> — adds a badge on the app; does <em>not</em> grant dashboard access.
                    </Alert>
                    {(selectedRole === 'admin' || selectedRole === 'moderator') && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This user will be able to log in to the admin dashboard.
                        </Alert>
                    )}
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>Role</InputLabel>
                        <Select value={selectedRole} label="Role" onChange={(e) => setSelectedRole(e.target.value)}>
                            <MenuItem value="none">Remove Role</MenuItem>
                            <MenuItem value="moderator">Moderator (dashboard access)</MenuItem>
                            <MenuItem value="admin">Admin (full dashboard access)</MenuItem>
                            <MenuItem value="doctor">Doctor (app badge only)</MenuItem>
                            <MenuItem value="midwife">Midwife (app badge only)</MenuItem>
                            <MenuItem value="nurse">Nurse (app badge only)</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRoleDialog({ open: false, user: null })}>Cancel</Button>
                    <Button variant="contained" onClick={handleSetRole}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Badge Dialog */}
            <Dialog open={badgeDialog.open} onClose={() => setBadgeDialog({ open: false, user: null })}>
                <DialogTitle>Expert Badge</DialogTitle>
                <DialogContent>
                    {badgeDialog.user?.hasExpertBadge ? (
                        <Typography>
                            Remove the expert badge from this user? Clinicians lose the in-app badge
                            and dashboard access is not granted.
                        </Typography>
                    ) : (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Grants an in-app badge only. Doctors, midwives, and nurses cannot
                                log in to this dashboard.
                            </Alert>
                            <FormControl fullWidth sx={{ mt: 1 }}>
                                <InputLabel>Badge</InputLabel>
                                <Select
                                    value={selectedBadgeRole}
                                    label="Badge"
                                    onChange={(e) => setSelectedBadgeRole(e.target.value)}
                                >
                                    <MenuItem value="doctor">Doctor</MenuItem>
                                    <MenuItem value="midwife">Midwife</MenuItem>
                                    <MenuItem value="nurse">Nurse</MenuItem>
                                </Select>
                            </FormControl>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBadgeDialog({ open: false, user: null })}>Cancel</Button>
                    <Button
                        variant="contained"
                        color={badgeDialog.user?.hasExpertBadge ? 'error' : 'success'}
                        onClick={() => handleSetExpertBadge(!badgeDialog.user?.hasExpertBadge)}
                    >
                        {badgeDialog.user?.hasExpertBadge ? 'Remove Badge' : 'Grant Badge'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Subscription Dialog */}
            <Dialog open={subscriptionDialog.open} onClose={() => setSubscriptionDialog({ open: false, user: null })}>
                <DialogTitle>Manage Subscription — {subscriptionDialog.user?.displayName || subscriptionDialog.user?.email}</DialogTitle>
                <DialogContent>
                    {subscriptionDialog.user?.subscriptionStatus && subscriptionDialog.user.subscriptionStatus !== 'free' ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Current plan: <strong>{subscriptionDialog.user.subscriptionStatus}</strong> (platform: {subscriptionDialog.user.subscriptionPlatform || 'unknown'})
                        </Alert>
                    ) : (
                        <Alert severity="warning" sx={{ mb: 2 }}>No active paid subscription</Alert>
                    )}
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>Grant Plan</InputLabel>
                        <Select value={selectedPlan} label="Grant Plan" onChange={(e) => setSelectedPlan(e.target.value)}>
                            <MenuItem value="premium">Premium (1 year)</MenuItem>
                            <MenuItem value="pro">Pro (1 year)</MenuItem>
                            <MenuItem value="family">Family (1 year)</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    {subscriptionDialog.user?.subscriptionStatus && subscriptionDialog.user.subscriptionStatus !== 'free' && (
                        <Button color="error" onClick={() => handleRevokeSubscription(subscriptionDialog.user)}>
                            Revoke Subscription
                        </Button>
                    )}
                    <Button onClick={() => setSubscriptionDialog({ open: false, user: null })}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={() => handleGrantSubscription(selectedPlan)}>
                        Grant Plan
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserManagement;
